<template>
  <div>
    <div class="card p-2">
      <TitleButton
        :showBtn="true"
        title="Money Receipt"
        :showAddNew="false"
        @onClickCloseButton="navigateToListPage"
      />

      <div class="row gy-1 pt-75 bg-blue-light rounded-8 mt-1 pb-2">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Client: </label>
          <AsyncSelect
              placeholder="Client Name, ID, Mobile No"
              v-model="selectedClient"
              :api-service="fetchContactProfileDropdownList"
              :additional-query="{ roles: 'client' }"
              :format-label="formatContactProfileLabel"
              :auto-select-first="true"
              label="name"
              :reduce="client => client"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Received Date</label>
          <input
            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
            tabindex="1"
            type="date"
            placeholder=""
            v-model="salesFormData.receipt_date"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Received In</label>
          <v-select
            v-model="salesFormData.account_head_id"
            placeholder="Select Cash & Bank Account"
            class="w-100"
            :options="accountHeads"
            label="name"
            :reduce="(name) => name.id"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Received Ref</label>
          <div class="input-group input-group-merge invoice-edit-input-group">
            <input
              v-model="salesFormData.receipt_reference"
              type="text"
              class="form-control invoice-edit-input"
              placeholder=""
            />
          </div>
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">R.Ref Date</label>
          <input
            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
            tabindex="1"
            type="date"
            placeholder=""
            v-model="salesFormData.receipt_reference_date"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">VDS Account</label>
          <v-select
              v-model="salesFormData.vat_challan_account_head_id"
              placeholder="Select VAT Challan Receivable"
              class="w-100"
              :options="vatChallanHeads"
              label="name"
              :reduce="(name) => name.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">AIT Accounts</label>
          <v-select
              v-model="salesFormData.ait_challan_account_head_id"
              placeholder="Select AIT Challan Receivable"
              class="w-100"
              :options="aitChallanHeads"
              label="name"
              :reduce="(name) => name.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Discount Account</label>
          <v-select
              v-model="salesFormData.discount_account_head_id"
              placeholder="Select Discount Account"
              class="w-100"
              :options="discountAccountHeads"
              label="name"
              :reduce="(name) => name.id"
          />
        </div>
      </div>

      <div class="mt-2"></div>

      <div v-if="!selectedClient" class="text-center mt-5">
        <p>Please select a Client to load due bills.</p>
      </div>

      <template v-else>
        <ClientWiseDueTable :dueBills="dueBills" @updateTotal="updateTotalAmount" />

        <div class="row mt-1">
          <div class="col-12">
            <div class="mb-2">
              <label for="note" class="form-label fw-bold">Memo</label>
              <textarea
                v-model="salesFormData.description"
                placeholder="Memo"
                class="form-control"
                rows="2"
                id="note"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="d-flex gap-1">
          <button
            :disabled="saveButtonLoader"
            @click="onClickOpenConfirm(false)"
            class="btn btn-primary"
          >
            Save
          </button>
          <button
            :disabled="saveButtonLoader"
            @click="onClickOpenConfirm(true)"
            class="btn btn-primary"
          >
            Save & Print
          </button>
          <button
            @click="navigateToListPage"
            class="btn btn-outline-secondary cancel-btn"
          >
            Cancel
          </button>
        </div>
      </template>
    </div>
    <ConfirmMoneyReceiptModal
      v-if="isConfirmModalOpen"
      :client-label="selectedClientLabel"
      :patient-count="selectedPatientCount"
      :bill-count="selectedBillCount"
      :total-due="selectedTotalDue"
      :collection-amount="totalAmount"
      @onConfirm="onConfirmSave"
      @onClose="isConfirmModalOpen = false"
    />
    <GlobalLoader />
  </div>
</template>

<script setup>
import { ref, onMounted, inject, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import TitleButton from "@/components/atom/TitleButton";
import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import ClientWiseDueTable from "@/components/molecule/company/inventory/pending-money-receipt-clientwise/ClientWiseDueTable.vue";
import ConfirmMoneyReceiptModal from "@/components/molecule/company/inventory/pending-money-receipt-clientwise/ConfirmMoneyReceiptModal.vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";

import handlePurchaseAndSales from "@/services/modules/purchase";
import handlePurchase from "@/services/modules/purchase";
import handleReceipt from "@/services/modules/receipt";
import handleDropdowns from "@/services/modules/dropdowns";
import handleCompany from "@/services/modules/company";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import { useQuery } from "@/services/utils/query";
import moneyReceiptHelper from "@/services/utils/pdf/moneyReceiptHelper";

const { salesFormData, storeInvoice, prepareSalesFormData, fetchMoneyReceipt } = handlePurchaseAndSales();
const { fetchCashAndBank, fetchClientWiseDueBills } = handleReceipt();
const { getAccountHeadBySlag } = handlePurchase();
const { fetchContactProfileDropdownList } = handleDropdowns();
const { fetchCompanyInfo } = handleCompany();
const { formatContactProfileLabel } = useAsyncDropdownHelper();
const { allParams } = useQuery(20);
const { generatePdf } = moneyReceiptHelper();

const route = useRoute();
const router = useRouter();
const params = allParams();
const companyId = computed(() => route.params.companyId);

const showError = inject("showError");
const showSuccess = inject("showSuccess");

const ROUTE_CLIENT_KEYS = ["redirect_route", "client_id", "client_name", "client_serial_no", "client_mobile_no"];

const getRouteClient = () => {
  if (! route.query.client_id) return null;
  return {
    id: Number(route.query.client_id),
    name: route.query.client_name,
    serial_no: route.query.client_serial_no,
    mobile_no: route.query.client_mobile_no
  };
}

const saveButtonLoader = ref(false);
const isConfirmModalOpen = ref(false);
const printAfterSave = ref(false);
const selectedClient = ref(getRouteClient());
const selectedClientLabel = ref('');
const dueBills = ref([]);
const totalAmount = ref(0);
const accountHeads = ref([]);
const companyInfo = ref({});
const moneyReceipt = ref({});
const vatChallanHeads = ref([]);
const aitChallanHeads = ref([]);
const discountAccountHeads = ref([]);

const selectedBills = computed(() => dueBills.value.filter(bill => bill.selected));
const selectedBillCount = computed(() => selectedBills.value.length);
const selectedPatientCount = computed(() => new Set(selectedBills.value.map(bill => bill.contact_profile_id)).size);
const selectedTotalDue = computed(() => selectedBills.value.reduce((sum, bill) => sum + Number(bill.due), 0));

const updateTotalAmount = (newTotal) => {
  totalAmount.value = newTotal;
};

const getAccountHead = () => {
  fetchCashAndBank(companyId.value).then(res => {
    if (res.status) accountHeads.value = res.data
  })
}

const onChangeClient = (client) => {
  if (!client) return
  selectedClientLabel.value = formatContactProfileLabel(client);
  getDueBills(client.id);
}

watch(selectedClient, (newClient) => {
  if (!newClient) return
  onChangeClient(newClient);
})

const getDueBills = (clientId) => {
  const query = `?company_id=${companyId.value}&contact_profile_client_id=${clientId}`;
  fetchClientWiseDueBills(query).then(res => {
    if (!res.status) {
      dueBills.value = [];
      return;
    }
    dueBills.value = res.data.pay_bill.map(bill => ({
      ...bill,
      payment: 0,
      vat_challan_amount: 0,
      ait_challan_amount: 0,
      discount_amount: 0,
      selected: false
    }));
  })
}

const getCompanyInfo = async () => {
  const res = await fetchCompanyInfo(companyId.value);
  if (!res.status) {
    showError('Something is wrong. Check your connectivity!!');
    return;
  }
  companyInfo.value = res.data;
}

const moneyReceiptPrint = (id) => {
  const companyQuery = `?company_id=${companyId.value}`;

  fetchMoneyReceipt(id, companyQuery).then((res) => {
    if (!res.status) {
      return;
    }
    moneyReceipt.value = res.data;
  }).then(() => {
    generatePdf(companyInfo.value, moneyReceipt.value)
  }).catch(() => {
    showError('Something is wrong. Check your connectivity!!')
  })
}

function onClickOpenConfirm(print = false) {
  if (!selectedBillCount.value) {
    showError('Please select at least one due bill');
    return;
  }

  printAfterSave.value = print;
  isConfirmModalOpen.value = true;
}

function onConfirmSave() {
  isConfirmModalOpen.value = false;
  saveButtonLoader.value = true;
  salesFormData.value.contact_profile_id = selectedClient.value.id;
  salesFormData.value.voucher_type = "receipt_voucher";
  salesFormData.value.txn_type = "invoice_receipt";

  const payload = prepareSalesFormData(selectedBills.value, "sale");
  storeInvoice(payload)
    .then((res) => {
      saveButtonLoader.value = false;
      if (! res.status) {
        showError(res.message);
        return;
      }
      showSuccess(res.message);
      getDueBills(selectedClient.value.id);
      if (printAfterSave.value) {
        moneyReceiptPrint(res.data.id);
      }
    })
    .catch(() => {
      saveButtonLoader.value = false;
    });
}

function navigateToListPage() {
  const query = Object.assign({}, route.query);
  ROUTE_CLIENT_KEYS.forEach(key => delete query[key]);
  router.push({
    name: route.query.redirect_route || "pending-money-receipt",
    params: params,
    query: query,
  });
}

onMounted(() => {
  if (selectedClient.value) onChangeClient(selectedClient.value);
  getAccountHead();
  getCompanyInfo();
  salesFormData.value.receipt_date = new Date().toISOString().split("T")[0];
  salesFormData.value.company_id = params.companyId;

  const companyQuery = `?company_id=${companyId.value}`;
  getAccountHeadBySlag("vat_challan", companyQuery).then(({ data }) => {
    if (data) vatChallanHeads.value = data;
    salesFormData.value.vat_challan_account_head_id = data.length ? data[0].id : null;
  });
  getAccountHeadBySlag("ait_challan", companyQuery).then(({ data }) => {
    if (data) aitChallanHeads.value = data;
    salesFormData.value.ait_challan_account_head_id = data.length ? data[0].id : null;
  });
  getAccountHeadBySlag("discount_account", companyQuery).then(({ data }) => {
    if (data) discountAccountHeads.value = data;
    salesFormData.value.discount_account_head_id = data.length ? data[0].id : null;
  });
});
</script>
